// HourlyBooking — fixed-slot (3h / 6h / 12h) room booking with per-room-type
// slot prices. Admin can set the slot prices here too.
import React, { useEffect, useState } from 'react';
import {
  Box, Container, Typography, MenuItem, Select, FormControl, InputLabel,
  TextField, Button, Card, CardContent, Chip, Snackbar, Alert, Divider,
  Table, TableBody, TableRow, TableCell, TableHead, FormControlLabel, Checkbox,
} from '@mui/material';
import config from '../../config';
import { getLoggedInUserId } from './userSession';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const token = sessionStorage.getItem('token');
const currency = company?.currency ?? '₹';
const auth = { headers: { Authorization: `Bearer ${token}` } };

const SLOTS = [3, 6, 12];
const priceFor = (rt, hours) =>
  hours === 3 ? rt?.hourlyPrice3 : hours === 6 ? rt?.hourlyPrice6 : rt?.hourlyPrice12;

export default function HourlyBooking() {
  const [hotels, setHotels] = useState([]);
  const [hotelId, setHotelId] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [priceEdits, setPriceEdits] = useState({}); // id -> {p3,p6,p12}
  const [savingPrices, setSavingPrices] = useState(false);

  const [roomType, setRoomType] = useState('');
  const [hours, setHours] = useState(3);
  const [startTime, setStartTime] = useState('');
  const [rooms, setRooms] = useState([]);
  const [roomNumber, setRoomNumber] = useState('');
  const [guestName, setGuestName] = useState('');
  const [mobile, setMobile] = useState('');
  const [paid, setPaid] = useState(false);
  const [booking, setBooking] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });

  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const loadRoomTypes = () => {
    fetch(`${config.API_BASE_URL}/hourly/roomtypes/${company?.id}`, auth)
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setRoomTypes(list);
        const edits = {};
        list.forEach((t) => {
          edits[t.id] = {
            p3: t.hourlyPrice3 || '',
            p6: t.hourlyPrice6 || '',
            p12: t.hourlyPrice12 || '',
          };
        });
        setPriceEdits(edits);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetch(`${config.API_BASE_URL}/Home/GetHotelsdropdown/${company?.id}`, auth)
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : [];
        setHotels(list);
        if (list.length) setHotelId(list[0].id);
      })
      .catch(() => {});
    loadRoomTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrices = async (t) => {
    const e = priceEdits[t.id] || {};
    setSavingPrices(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/hourly/roomtype-prices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          id: t.id,
          hourlyPrice3: Number(e.p3) || 0,
          hourlyPrice6: Number(e.p6) || 0,
          hourlyPrice12: Number(e.p12) || 0,
        }),
      });
      if (!res.ok) throw new Error();
      notify('Slot prices saved.');
      loadRoomTypes();
    } catch {
      notify('Failed to save prices.', 'error');
    } finally {
      setSavingPrices(false);
    }
  };

  const findAvailable = async () => {
    setRooms([]);
    setRoomNumber('');
    if (!hotelId || !roomType || !startTime) {
      notify('Please select hotel, room type and start time.', 'warning');
      return;
    }
    try {
      const url = `${config.API_BASE_URL}/hourly/available?companyId=${company?.id}&hotelId=${hotelId}&roomType=${encodeURIComponent(
        roomType,
      )}&start=${encodeURIComponent(startTime)}&hours=${hours}`;
      const res = await fetch(url, auth);
      const d = await res.json();
      setRooms(Array.isArray(d) ? d : []);
      if (!Array.isArray(d) || d.length === 0) notify('No rooms are free in this slot.', 'info');
    } catch {
      notify('Failed to load rooms.', 'error');
    }
  };

  const rt = roomTypes.find((t) => t.roomtype === roomType);
  const slotPrice = priceFor(rt, hours) || 0;

  const placeBooking = async () => {
    if (!roomNumber || !guestName.trim() || mobile.trim().length !== 10) {
      notify('Room, guest name and 10-digit mobile are required.', 'warning');
      return;
    }
    if (!slotPrice) {
      notify('Price is not set for this room-type/slot.', 'warning');
      return;
    }
    setBooking(true);
    try {
      // Book through the dedicated hourly endpoint. It writes the SAME records
      // as a daily booking (HotelBooking + Bookingroomdetail + HotelInv +
      // BookingLadger) — just with time-based check-in/out and the fixed slot
      // price, and without needing a package (hourly is room-type based).
      // Send the start time as a local datetime string so the slot time is
      // preserved (not shifted by UTC).
      const pad = (n) => String(n).padStart(2, '0');
      const startDt = new Date(startTime);
      const startLocal =
        `${startDt.getFullYear()}-${pad(startDt.getMonth() + 1)}-${pad(
          startDt.getDate(),
        )}T${pad(startDt.getHours())}:${pad(startDt.getMinutes())}:00`;

      const payload = {
        CompanyId: company?.id,
        HotelId: Number(hotelId),
        RoomType: roomType,
        RoomNumber: Number(roomNumber),
        Hours: hours,
        StartTime: startLocal,
        Price: slotPrice,
        GuestName: guestName.trim(),
        Mobile: mobile.trim(),
        UserId: getLoggedInUserId(),
        Paid: paid,
      };

      const res = await fetch(`${config.API_BASE_URL}/hourly/book`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let result;
      try {
        result = JSON.parse(text);
      } catch {
        result = { message: text };
      }
      if (!res.ok || result?.error) {
        notify(result?.error || result?.message || 'Booking failed.', 'error');
        return;
      }
      notify('Hourly booking done.');
      setRoomNumber('');
      setGuestName('');
      setMobile('');
      setPaid(false);
      setRooms([]);
    } catch {
      notify('Booking failed.', 'error');
    } finally {
      setBooking(false);
    }
  };

  return (
    <Container maxWidth='md' sx={{ py: 3 }}>
      <Typography variant='h5' fontWeight={700} mb={2}>
        Hourly Booking
      </Typography>

      {/* ── Slot prices editor ── */}
      <Card variant='outlined' sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Typography fontWeight={700} mb={1}>
            Slot Prices (per room type)
          </Typography>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Room Type</TableCell>
                <TableCell>3 hr</TableCell>
                <TableCell>6 hr</TableCell>
                <TableCell>12 hr</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {roomTypes.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.roomtype}</TableCell>
                  {['p3', 'p6', 'p12'].map((k) => (
                    <TableCell key={k}>
                      <TextField
                        size='small'
                        type='number'
                        value={priceEdits[t.id]?.[k] ?? ''}
                        onChange={(e) =>
                          setPriceEdits((prev) => ({
                            ...prev,
                            [t.id]: { ...prev[t.id], [k]: e.target.value },
                          }))
                        }
                        sx={{ width: 90 }}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <Button size='small' disabled={savingPrices} onClick={() => savePrices(t)}>
                      Save
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {roomTypes.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align='center'>
                    No room types.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* ── New hourly booking ── */}
      <Card variant='outlined' sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography fontWeight={700} mb={2}>
            New Hourly Booking
          </Typography>
          <Box display='flex' gap={2} flexWrap='wrap' mb={2}>
            <FormControl size='small' sx={{ minWidth: 180 }}>
              <InputLabel>Hotel</InputLabel>
              <Select label='Hotel' value={hotelId} onChange={(e) => setHotelId(e.target.value)}>
                {hotels.map((h) => (
                  <MenuItem key={h.id} value={h.id}>
                    {h.hotelName || h.name || `Hotel ${h.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size='small' sx={{ minWidth: 180 }}>
              <InputLabel>Room Type</InputLabel>
              <Select label='Room Type' value={roomType} onChange={(e) => setRoomType(e.target.value)}>
                {roomTypes.map((t) => (
                  <MenuItem key={t.id} value={t.roomtype}>
                    {t.roomtype}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size='small' sx={{ minWidth: 120 }}>
              <InputLabel>Slot</InputLabel>
              <Select label='Slot' value={hours} onChange={(e) => setHours(Number(e.target.value))}>
                {SLOTS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s} hours
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size='small'
              type='datetime-local'
              label='Start time'
              InputLabelProps={{ shrink: true }}
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
            <Button variant='outlined' onClick={findAvailable}>
              Find Rooms
            </Button>
          </Box>

          {rt && (
            <Typography variant='body2' color='text.secondary' mb={1}>
              Selected slot price:{' '}
              <b>
                {currency} {Number(slotPrice).toFixed(2)}
              </b>
              {!slotPrice && ' (set this slot price above first)'}
            </Typography>
          )}

          {rooms.length > 0 && (
            <Box mb={2}>
              <Typography variant='body2' fontWeight={600} mb={0.5}>
                Available rooms:
              </Typography>
              <Box display='flex' gap={1} flexWrap='wrap'>
                {rooms.map((r) => (
                  <Chip
                    key={r.roomnumber}
                    label={r.roomName ? r.roomName : `Room ${r.roomnumber}`}
                    color={String(roomNumber) === String(r.roomnumber) ? 'primary' : 'default'}
                    variant={String(roomNumber) === String(r.roomnumber) ? 'filled' : 'outlined'}
                    onClick={() => setRoomNumber(r.roomnumber)}
                  />
                ))}
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Box display='flex' gap={2} flexWrap='wrap' alignItems='center'>
            <TextField
              size='small'
              label='Guest Name'
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
            />
            <TextField
              size='small'
              label='Mobile'
              inputProps={{ maxLength: 10 }}
              value={mobile}
              onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
            />
            <FormControlLabel
              control={<Checkbox checked={paid} onChange={(e) => setPaid(e.target.checked)} />}
              label='Paid (Cash)'
            />
            <Box flex={1} />
            <Button
              variant='contained'
              disabled={booking || !roomNumber}
              onClick={placeBooking}>
              {booking ? 'Booking...' : `Book (${currency} ${Number(slotPrice).toFixed(0)})`}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
